/**
 * Queue Metrics Collector - Tracks offline queue performance metrics
 * Integrates with OfflineQueueManager to collect:
 * - Replay duration (start/end times, duration by operation type)
 * - Retry success rates (success/failure counts, attempts distribution)
 * - Conflict frequency (types, resolution strategies, by operation type)
 * - Batch compression metrics (ratios, storage saved, performance)
 */

import { OPERATION_TYPES } from './offlineQueueManager';

const METRICS_STORAGE_KEY = 'quilline_queue_metrics';
const MAX_METRICS_HISTORY = 1000; // Keep last 1000 metric entries

export class QueueMetricsCollector {
  constructor() {
    this.metrics = this.loadMetrics();
  }

  /**
   * Load metrics from localStorage
   */
  loadMetrics() {
    try {
      const stored = localStorage.getItem(METRICS_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('[MetricsCollector] Failed to load metrics:', error);
    }

    return {
      replayDurations: [],
      retryAttempts: [],
      conflicts: [],
      compressionMetrics: [],
      operationStats: {
        [OPERATION_TYPES?.BOOKMARK]: { success: 0, failed: 0, total: 0 },
        [OPERATION_TYPES?.CLAP]: { success: 0, failed: 0, total: 0 },
        [OPERATION_TYPES?.COMMENT]: { success: 0, failed: 0, total: 0 },
        [OPERATION_TYPES?.STORY_UPDATE]: { success: 0, failed: 0, total: 0 },
      },
    };
  }

  /**
   * Save metrics to localStorage
   */
  saveMetrics() {
    try {
      localStorage.setItem(METRICS_STORAGE_KEY, JSON.stringify(this.metrics));
    } catch (error) {
      console.error('[MetricsCollector] Failed to save metrics:', error);
    }
  }

  /**
   * Track replay duration
   */
  trackReplayDuration(operationType, startTime, endTime, operationCount, success) {
    const duration = endTime - startTime;
    const entry = {
      timestamp: Date.now(),
      date: new Date()?.toISOString(),
      operationType,
      duration,
      operationCount,
      success,
      avgDurationPerOp: operationCount > 0 ? duration / operationCount : 0,
    };

    this.metrics?.replayDurations?.push(entry);
    this.trimHistory('replayDurations');
    this.saveMetrics();
  }

  /**
   * Track retry attempt
   */
  trackRetryAttempt(operationType, attemptNumber, success, error = null) {
    const entry = {
      timestamp: Date.now(),
      date: new Date()?.toISOString(),
      operationType,
      attemptNumber,
      success,
      error: error ? error?.message : null,
    };

    this.metrics?.retryAttempts?.push(entry);
    
    // Update operation stats
    if (this.metrics?.operationStats?.[operationType]) {
      this.metrics.operationStats[operationType].total++;
      if (success) {
        this.metrics.operationStats[operationType].success++;
      } else {
        this.metrics.operationStats[operationType].failed++;
      }
    }

    this.trimHistory('retryAttempts');
    this.saveMetrics();
  }

  /**
   * Track conflict
   */
  trackConflict(operationType, conflictType, resolutionStrategy, tableName) {
    const entry = {
      timestamp: Date.now(),
      date: new Date()?.toISOString(),
      operationType,
      conflictType,
      resolutionStrategy,
      tableName,
    };

    this.metrics?.conflicts?.push(entry);
    this.trimHistory('conflicts');
    this.saveMetrics();
  }

  /**
   * Track compression metrics
   */
  trackCompression(operationType, originalSize, compressedSize, compressionTime) {
    const compressionRatio = originalSize > 0 ? (compressedSize / originalSize) * 100 : 100;
    const storageSaved = originalSize - compressedSize;

    const entry = {
      timestamp: Date.now(),
      date: new Date()?.toISOString(),
      operationType,
      originalSize,
      compressedSize,
      compressionRatio,
      storageSaved,
      compressionTime,
    };

    this.metrics?.compressionMetrics?.push(entry);
    this.trimHistory('compressionMetrics');
    this.saveMetrics();
  }

  /**
   * Trim history to prevent unbounded growth
   */
  trimHistory(metricType) {
    if (this.metrics?.[metricType]?.length > MAX_METRICS_HISTORY) {
      this.metrics[metricType] = this.metrics?.[metricType]?.slice(-MAX_METRICS_HISTORY);
    }
  }

  /**
   * Get metrics for a specific time range
   */
  getMetricsInRange(startDate, endDate) {
    const start = new Date(startDate)?.getTime();
    const end = new Date(endDate)?.getTime();

    const filterByRange = (entries) => {
      return entries?.filter(entry => {
        const entryTime = entry?.timestamp || new Date(entry.date)?.getTime();
        return entryTime >= start && entryTime <= end;
      });
    };

    return {
      replayDurations: filterByRange(this.metrics?.replayDurations),
      retryAttempts: filterByRange(this.metrics?.retryAttempts),
      conflicts: filterByRange(this.metrics?.conflicts),
      compressionMetrics: filterByRange(this.metrics?.compressionMetrics),
      operationStats: this.metrics?.operationStats,
    };
  }

  /**
   * Get all metrics
   */
  getAllMetrics() {
    return this.metrics;
  }

  /**
   * Clear all metrics
   */
  clearMetrics() {
    this.metrics = {
      replayDurations: [],
      retryAttempts: [],
      conflicts: [],
      compressionMetrics: [],
      operationStats: {
        [OPERATION_TYPES?.BOOKMARK]: { success: 0, failed: 0, total: 0 },
        [OPERATION_TYPES?.CLAP]: { success: 0, failed: 0, total: 0 },
        [OPERATION_TYPES?.COMMENT]: { success: 0, failed: 0, total: 0 },
        [OPERATION_TYPES?.STORY_UPDATE]: { success: 0, failed: 0, total: 0 },
      },
    };
    this.saveMetrics();
  }

  /**
   * Export metrics as JSON
   */
  exportAsJSON() {
    return JSON.stringify(this.metrics, null, 2);
  }

  /**
   * Export metrics as CSV
   */
  exportAsCSV(metricType) {
    const data = this.metrics?.[metricType];
    if (!data || data?.length === 0) {
      return '';
    }

    // Get headers from first entry
    const headers = Object.keys(data?.[0]);
    const csvRows = [headers?.join(',')];

    // Add data rows
    data?.forEach(entry => {
      const values = headers?.map(header => {
        const value = entry?.[header];
        // Escape commas and quotes
        if (typeof value === 'string' && (value?.includes(',') || value?.includes('"'))) {
          return `"${value?.replace(/"/g, '""')}"`;
        }
        return value;
      });
      csvRows?.push(values?.join(','));
    });

    return csvRows?.join('\n');
  }
}

// Singleton instance
export const metricsCollector = new QueueMetricsCollector();
